import { createFeatureSelector, createSelector } from '@ngrx/store';
import { smartRecoFeatureKey } from '../smart-reco-feature-key';
import { SmartRecoState } from '../smart-reco.state';
import { CriteriaType } from '@app/shared/utils/enums/criteria-type.enum';
import { filterAndFormatCoverageCriterias, isIndividualis, ProductPillar, ProductType } from '@app/shared/utils';
import { selectSmartRecoPetSummary } from '@app/pages/smart-reco/store/selectors/smart-reco-steps.selectors';
import { Product } from '@app/core/models';
import { selectAllProductsSuccess } from '@app/store/products/products.selectors';
import { SystemPreferenceCode } from '@app/shared/utils/enums/system-preference-code';

/**
 * Number of products we want to display
 */
const DRY_PRODUCTS_MAX_LENGTH = 3;
const WET_PRODUCTS_MAX_LENGTH = 5;
const TREATS_MAX_LENGTH = 5;

const selectState = createFeatureSelector<SmartRecoState>(smartRecoFeatureKey);

export const selectSmartRecoProductsWithoutFilterCriteria = createSelector(selectState, (state) => {
  return state.recommendation?.products?.slice(0, DRY_PRODUCTS_MAX_LENGTH);
});

export const selectRecoHasIndividualis = (currentSystemPreferenceCode: SystemPreferenceCode) =>
  createSelector(selectSmartRecoProductsWithoutFilterCriteria, selectSmartRecoPetSummary, (products, petSummary) => {
    return isIndividualis(products, petSummary, currentSystemPreferenceCode);
  });

export const selectRecoProducts = createSelector(selectState, selectSmartRecoPetSummary, (state, petSummary) => {
  return state.recommendation?.products
    ?.map((product) => ({
      ...product,
      coverage: filterAndFormatCoverageCriterias(product.coverage, petSummary),
    }))
    ?.slice(0, DRY_PRODUCTS_MAX_LENGTH);
});

export const selectIsIndividualisSelected = createSelector(selectState, (state) => state.isIndividualisSelected);

/**
 * Emits true if the recommendation is accurate enough,
 * meaning the coverage is good enough
 */
export const selectIsRecoAccurate = createSelector(selectRecoProducts, (productsRecommendation) =>
  productsRecommendation?.every((product) => {
    const idealPmeans = 0.65;
    const diseasesCriterias = product?.coverage?.criterias?.filter((criteria) => criteria.type === CriteriaType.Diseases) || [];
    const pmeansCriteriasIsOk = diseasesCriterias.every((diseaseCriteria) => diseaseCriteria.pmean >= idealPmeans);
    const pmeansProductsIsOk = product.coverage.pmean >= idealPmeans;
    return pmeansProductsIsOk && pmeansCriteriasIsOk;
  }),
);

export const selectRecoProductsCostPerDay = createSelector(selectState, (state) => state.productsCostPerDay);

export const selectRecoNotRecommendedCostPerDay = createSelector(selectState, (state) => state.notRecommendedProductCostPerDay);
/**
 * Selects catalog products
 */
export const selectRecoCatalogProductsAutocomplete = createSelector(selectState, (state) =>
  state.catalogProducts.map((product) => ({
    label: product.name,
    value: product,
  })),
);
/**
 * Selects not recommended product
 */
export const selectRecoNotRecommendedProduct = createSelector(selectState, (state) => state.notRecommendedProduct);

export const selectRecoProductRecommendationErrorType = createSelector(selectState, (state) => state.recommendationErrorType);
export const selectRecoProductRecommendationRetryCount = createSelector(selectState, (state) => state.retryCount);

export const selectRecoProductRecommendationSessionIdError = createSelector(selectState, (state) => state.recommendationSessionIdError);

/**
 * Selects recommended and not recommended products
 */
export const selectRecoProductsAndNotRecommendedProduct = createSelector(
  selectRecoProducts,
  selectRecoNotRecommendedProduct,
  (recommendedProducts, notRecommendedProduct) =>
    notRecommendedProduct ? [...recommendedProducts, notRecommendedProduct] : recommendedProducts,
);

export const selectRelatedWetProducts = (dryProduct: Product) =>
  createSelector(selectAllProductsSuccess, (allProductList: Product[]) => {
    return allProductList
      .filter(() => !!dryProduct?.territory)
      .filter((product: Product) => {
        return dryProduct?.relatedProductsCode?.includes(product.id) ?? [];
      })
      .filter((product: Product) => product.productType === ProductType.Wet)
      .filter((product: Product) => {
        const pillars: ProductPillar[] =
          dryProduct.territory == 'birth and growth' ? [ProductPillar.VET, ProductPillar.SPTRETAIL] : [ProductPillar.VET];
        return pillars.includes(product.pillar);
      })
      .slice(0, WET_PRODUCTS_MAX_LENGTH);
  });

export const selectRelatedTreats = (dryProduct: Product) =>
  createSelector(selectAllProductsSuccess, (allProductList: Product[]) => {
    return allProductList
      .filter(() => !!dryProduct?.relatedTreatsCode)
      .filter((product: Product) => {
        return (dryProduct?.relatedTreatsCode?.includes(product.id) && product.productType === ProductType.Treats) ?? [];
      })
      .slice(0, TREATS_MAX_LENGTH);
  });
